import { FoodsIcon, FoodsIconButton, FoodsTextInput } from "@foodpilot/foods";
import { Box, IconButton, Stack, TextField, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ComponentAppearance } from "src/components/appearance";

type SearchBarProps = {
    search: string;
    setSearch: (newSearch: string) => void;
    placeholder: string;
    autoFocus?: boolean;
    appearance?: ComponentAppearance;
};

export const SearchBar = (props: SearchBarProps) => {
    const { search, setSearch, placeholder, autoFocus = false, appearance = "legacy" } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const isProductUxV2 = appearance === "product-v2";
    const legacyDivider = theme.custom.strokes?.containerDivider;

    if (!isProductUxV2) {
        return (
            <Box
                sx={{
                    alignItems: "center",
                    backgroundColor: theme.palette.background.paper,
                    border: `${legacyDivider?.width ?? theme.spacing(0.125)} ${legacyDivider?.style ?? "solid"} ${theme.custom.grey[500]}`,
                    borderRadius: theme.spacing(3),
                    boxShadow: `0 ${theme.spacing(0.25)} ${theme.spacing(1)} 0 ${theme.custom.grey[500]}`,
                    display: "flex",
                    gap: theme.spacing(1),
                    height: theme.spacing(5),
                    justifyContent: "space-between",
                    paddingInline: theme.spacing(2.5),
                    width: "100%",
                }}
            >
                <TextField
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- filter popovers open after a user action
                    autoFocus={autoFocus}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                    placeholder={placeholder}
                    type="search"
                    value={search}
                    sx={{
                        width: "100%",
                        "& > .MuiInputBase-root": {
                            "& > fieldset": { border: "none" },
                            "& > input": { padding: 0 },
                        },
                    }}
                />
                <Stack direction="row" alignItems="center" gap={theme.spacing(1)}>
                    {search !== "" && (
                        <IconButton
                            aria-label={t("effacer_la_recherche", "Clear search")}
                            onClick={() => setSearch("")}
                            size="small"
                            sx={{ padding: 0 }}
                        >
                            <FoodsIcon size={2} icon="close" />
                        </IconButton>
                    )}
                    <FoodsIcon size={3} icon="search" />
                </Stack>
            </Box>
        );
    }

    return (
        <FoodsTextInput
            value={search}
            onChange={setSearch}
            ExtraInputProps={{
                type: "search",
                placeholder,
                // Filter popovers opt in to autoFocus after a user action.
                autoFocus,
                onKeyDown: (event) => event.stopPropagation(),
                endAdornment: (
                    <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)}>
                        {search !== "" && (
                            <FoodsIconButton
                                size="xs"
                                icon="close"
                                buttonProps={{
                                    "aria-label": t("effacer_la_recherche", "Clear search"),
                                    onClick: () => setSearch(""),
                                }}
                            />
                        )}
                        <FoodsIcon size={3} icon={"search"} />
                    </Stack>
                ),
            }}
        />
    );
};
